import { Box, Typography } from "@material-ui/core";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { RouterProps } from "react-router";
import clsx from "clsx";
import {
  Header,
  HotelSplitMapView,
  useDeviceTypes,
  B2BLoadingPopup,
  ActionButton,
} from "halifax";
import dayjs from "dayjs";
import {
  CorpSessionInfo,
  IIdLodgings,
  NAVIGATED_BACK_TO_PACKAGE_HOTEL_LIST,
  PackagesEntryTypeEnum,
} from "redmond";
import queryStringParser from "query-string";
import {
  CDN_ASSETS,
  getCurrentCorpAccount,
  isCorpTenant,
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
} from "@capone/common";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";
import { PackagesHotelsAvailabilityConnectorProps } from "./container";
import { config } from "../../api/config";
import { PATH_HOME } from "../../utils/paths";
import { ClientContext } from "../../App";
import { RewardsAccountSelection } from "../rewards/components";
// import { TravelWalletDrawer } from "../travel-wallet/components";
import { PORTAL_TITLE, SELECT_HOTEL_TITLE } from "../../lang/textConstants";
import "./styles.scss";
import { AvailabilityList } from "./components/AvailabilityList";
import { AvailabilityMap } from "./components/AvailabilityMap";
import { PackagesAvailabilityCallState } from "./reducer/state";
import * as constants from "./textConstants";
import { MobilePackagesHotelAvailability } from "./components/MobilePackagesHotelAvailability";
import { TravelWalletDrawer } from "../travel-wallet/components";
import { AvailabilityErrorModal } from "./components/AvailabilityErrorModal";
import {
  parseQueryFromString,
  parseQueryString,
} from "../search/utils/parseQueryString";
import { PACKAGES_HOMEPAGE_HEADER_ACCOUNT_NAV_SLOT_ID } from "../../constants/homepageHeaderAccountNav";
import {
  PACKAGES_LIST_OPTIMIZATIONS_V1,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import { trackEvent } from "../../api/v0/trackEvent";

export interface IPackagesHotelsAvailability
  extends PackagesHotelsAvailabilityConnectorProps,
    RouterProps {}

export const PackagesHotelsAvailability = (
  props: IPackagesHotelsAvailability
) => {
  const {
    history,
    fetchTravelWalletDetails,
    fetchInitialPackagesAvailability,
    stopFetchMorePackagesAvailability,
    packagesAvailabilityCallState,
    largestValueAccount,
    untilDate,
    fromDate,
    location,
    setPackageEntryPoint,
    rewardsAccounts,
    fetchTravelWalletCreditHistory,
  } = props;
  const [mobileMapOpen, setMobileMapOpen] = React.useState(false);
  const [currentUrl, setCurrentUrl] = React.useState(history.location.search);

  const { logo, sessionInfo, showCoreHomepage } = useContext(ClientContext);
  const { matchesMobile } = useDeviceTypes();
  const [locationName, setLocationName] = React.useState("");
  const [isSearchTermLodging, setIsSearchTermLodging] = React.useState(false);
  // If search term is location (ex: Toronto) vs if search term is point of interest (ex: Toronto Zoo)
  const [isSearchTermPoint, setIsSearchTermPoint] = React.useState(false);
  const [showFullMap, setShowFullMap] = React.useState(false);

  const parsedQueryString = parseQueryString(history);

  const expState = useExperiments();

  const packagesListEnhancementsEnabled =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_LIST_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  React.useEffect(() => {
    if (location) {
      const [locationName] = location?.label ? location.label.split(",") : [];
      setLocationName(locationName);
      const placeTypes = location
        ? (location.id as IIdLodgings).lodgingSelection.placeTypes
        : [];

      setIsSearchTermLodging(placeTypes.includes("lodging"));
      setIsSearchTermPoint(
        !placeTypes.includes("locality") &&
          !placeTypes.includes("political") &&
          !placeTypes.includes("country")
      );
    }
  }, [location]);

  useEffect(() => {
    fetchInitialPackagesAvailability(history);

    return () => {
      stopFetchMorePackagesAvailability();
    };
  }, []);

  useEffect(() => {
    if (history.location.search !== currentUrl) {
      const currShowMap = parsedQueryString?.showMap;
      const prevShowMap = parseQueryFromString(currentUrl).showMap;

      if (!parsedQueryString?.isTravelApp || prevShowMap === currShowMap) {
        fetchInitialPackagesAvailability(history);
        setCurrentUrl(history.location.search);
      }
    }
  }, [history.location.search]);

  const queryString = useMemo(
    () => queryStringParser.parse(history.location.search),
    [history.location.search]
  );
  const { entryPoint } = queryString;

  useEffect(() => {
    if (entryPoint) {
      setPackageEntryPoint(entryPoint as PackagesEntryTypeEnum);
    }
  }, [entryPoint]);

  useEffect(() => {
    fetchTravelWalletDetails();
    fetchTravelWalletCreditHistory();
    // TODO: Find out why window.scrollTo requires a setTimeout; it's needed so that the browser nav button works with scrollTo
    document.title = SELECT_HOTEL_TITLE;
    setTimeout(() => window.scrollTo(0, 0), 0);
    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  const renderDesktopView = useCallback(() => {
    const desktopHeaderRight = showCoreHomepage ? (
      <Box
        className="desktop-hotel-availability-homepage-account-nav"
        id={PACKAGES_HOMEPAGE_HEADER_ACCOUNT_NAV_SLOT_ID}
      />
    ) : (
      <Box className="desktop-hotel-availability-rewards-account-contents">
        <RewardsAccountSelection
          className={clsx("b2b hide-balance-border")}
          popoverClassName="b2b"
        />
        <TravelWalletDrawer />
      </Box>
    );

    if (packagesListEnhancementsEnabled) {
      return (
        <div className="packages-hotels-availability-container no-map">
          <Header
            className="rewards-components-section"
            left={
              <Box className="rewards-account-section-left-content">
                <Box
                  className="logo"
                  onClick={() =>
                    showFullMap ? undefined : history.push(PATH_HOME)
                  }
                >
                  {showFullMap ? (
                    <ActionButton
                      className="back-to-list"
                      message={constants.BACK_TO_LIST}
                      onClick={() => {
                        setShowFullMap(false);
                        trackEvent({
                          eventName: NAVIGATED_BACK_TO_PACKAGE_HOTEL_LIST,
                          properties: {},
                        });
                      }}
                      defaultStyle="h4r-secondary"
                    />
                  ) : (
                    logo
                  )}
                </Box>
                <Box className="rewards-account-section-travel-details">
                  <Typography variant="body1" tabIndex={0}>
                    {constants.CHOOSE_HOTEL_TEXT(
                      locationName,
                      isSearchTermLodging,
                      isSearchTermPoint
                    )}
                  </Typography>
                  {fromDate && untilDate ? (
                    <Typography variant="body2">
                      {constants.DATES_TEXT(
                        dayjs(fromDate).format("ddd, MMM DD"),
                        dayjs(untilDate).format("ddd, MMM DD")
                      )}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            }
            right={desktopHeaderRight}
          />
          <AvailabilityList
            showFullMap={showFullMap}
            setShowFullMap={setShowFullMap}
          />
        </div>
      );
    }

    return (
      <HotelSplitMapView
        className={clsx(
          "packages-hotels-availability-container",
          config.TENANT
        )}
        header={
          // TODO: Make this common component to be used throughout Packages Funnel?
          <Header
            className="rewards-components-section"
            left={
              <Box className="rewards-account-section-left-content">
                <Box className="logo" onClick={() => history.push(PATH_HOME)}>
                  {logo}
                </Box>
                <Box className="rewards-account-section-travel-details">
                  <Typography variant="body1" tabIndex={0}>
                    {constants.CHOOSE_HOTEL_TEXT(
                      locationName,
                      isSearchTermLodging,
                      isSearchTermPoint
                    )}
                  </Typography>
                  {fromDate && untilDate ? (
                    <Typography variant="body2">
                      {constants.DATES_TEXT(
                        dayjs(fromDate).format("ddd, MMM DD"),
                        dayjs(untilDate).format("ddd, MMM DD")
                      )}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            }
            right={desktopHeaderRight}
          />
        }
        leftClassName={clsx("list-section")}
        left={<AvailabilityList />} // [Packages TO-DO]: Remove, lodgings will be passed through container redux state when we set using packages API
        rightClassName={clsx("map-section")}
        right={<AvailabilityMap />} // [Packages TO-DO]: Remove, lodgings will be passed through container redux state when we set using packages API
      />
    );
  }, [
    packagesListEnhancementsEnabled,
    locationName,
    isSearchTermLodging,
    isSearchTermPoint,
    fromDate,
    untilDate,
    showFullMap,
    showCoreHomepage,
  ]);

  const renderMobileView = () => (
    <Box
      className={clsx("packages-hotels-availability-container", config.TENANT, {
        "mobile-map": mobileMapOpen,
      })}
    >
      <MobilePackagesHotelAvailability onShowMap={setMobileMapOpen} />
    </Box>
  );

  const isCorporate = isCorpTenant(config.TENANT);
  const isPrimaryUser =
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "PR";
  const showEarnEnhancement =
    !!largestValueAccount && !!largestValueAccount.earn.hotelsMultiplier;

  const currentAccount = getCurrentCorpAccount(rewardsAccounts, sessionInfo);

  const getCapOneLoadingSecondaryText = () => {
    if (isCorporate && !isPrimaryUser) {
      return constants.FETCHING_AVAILABILITY_CORPORATE_SECONDARY_TEXT;
    }
    if (showEarnEnhancement) {
      const accountToDisplayOnLoading = isCorpTenant(config.TENANT)
        ? currentAccount || largestValueAccount
        : largestValueAccount;
      return constants.EARN_ENHANCEMENT_SUBTITLE(
        accountToDisplayOnLoading.earn.hotelsMultiplier,
        accountToDisplayOnLoading.productDisplayName,
        accountToDisplayOnLoading.rewardsBalance.currencyDescription ??
          accountToDisplayOnLoading.rewardsBalance.currency,
        parsedQueryString?.entryPoint === "flight_list"
          ? accountToDisplayOnLoading.earn.flightsMultiplier
          : undefined
      );
    }
    if (
      largestValueAccount?.productDisplayName.toLowerCase().includes("paradise")
    ) {
      return constants.PARADISE_SUBTITLE;
    }

    return constants.FETCHING_AVAILABILITY_SECONDARY_TEXT;
  };
  return (
    <>
      <B2BLoadingPopup
        open={
          packagesAvailabilityCallState ===
          PackagesAvailabilityCallState.InitialSearchCallInProcess
        }
        message={
          parsedQueryString?.entryPoint === "flight_list"
            ? constants.FETCHING_PACKAGES_AVAILABILITY_TEXT
            : constants.FETCHING_AVAILABILITY_TEXT
        }
        secondaryMessage={getCapOneLoadingSecondaryText()}
        image={CDN_ASSETS.capone.searchLoading.hotels}
        className={clsx("hotel-search-loading-popup earn-enhancement")}
        popupSize={matchesMobile ? "mobile" : "desktop"}
        onClose={() => {
          travelAppInterface.sendEvent({
            name: TravelAppInterfaceEventName.SEARCH_LOADED,
            funnel: TravelAppInterfaceFunnelType.PACKAGES,
          });
        }}
        isXmod={isXmodRedesign}
      />

      <AvailabilityErrorModal />
      <Box
        className={clsx("package-hotels-availability-root", {
          "full-width": !matchesMobile,
        })}
      >
        {matchesMobile ? renderMobileView() : renderDesktopView()}
      </Box>
    </>
  );
};
